import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useEffect, useMemo, useState } from "react";
import { Resolver, useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  InputLabel,
  Stack,
  Typography,
} from "@mui/material";
import { BarChartOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import _ from "lodash";

import MainCard from "components/MainCard";
import DatePickerElement from "components/DatePickerElement";
import { SelectField } from "components/@extended/SelectField";

import { IIssuedItemChart, IIssuedItemChartGridFilter } from "../api/types";
import { GetIssuedItemChart } from "../api/hooks";

/* ------------------------------------------------------------------ */

export default function IssuedItemChart() {
  /* ----------------------- Form setup -------------------------- */

  const resolver: Resolver<IIssuedItemChartGridFilter> = async (values) => {
    const errors: any = {};
    if (values.fromYear && values.toYear) {
      const from = dayjs(values.fromYear);
      const to = dayjs(values.toYear);
      if (from.isValid() && to.isValid() && from.isAfter(to, "year")) {
        errors.toYear = {
          type: "validate",
          message: "To Year must be greater than or equal to From Year",
        };
      }
    }
    return {
      values,
      errors,
    };
  };

  // const { control, handleSubmit } = useForm<IIssuedItemChartGridFilter>({
  //   resolver,
  //   defaultValues: {
  //     fromYear: dayjs().subtract(1, "year").format("YYYY"),
  //     toYear: dayjs().format("YYYY"),
  //     actionType: 1,
  //   },
  // });

  /* ----------------------- Filter state ------------------------ */

  const [filterModel, setFilterModel] = useState<IIssuedItemChartGridFilter>({
    fromYear: dayjs().subtract(1, "year").format("YYYY"),
    toYear: dayjs().format("YYYY"),
    actionType: 1,
  });

  /* ----------------------- API call ---------------------------- */

  const {
    data: response,
    isFetching,
    isLoading,
  } = GetIssuedItemChart(filterModel);

  const [data, setData] = useState<IIssuedItemChart[]>([]);
  useEffect(() => {
    if (response?.status == 200) {
      setData(response?.apiData);
    }
  }, [response]);
  // 1. Add watch and setValue
  const { control, handleSubmit, watch, setValue } =
    useForm<IIssuedItemChartGridFilter>({
      resolver,
      defaultValues: {
        fromYear: dayjs().subtract(1, "year").format("YYYY"),
        toYear: dayjs().format("YYYY"),
        actionType: 1,
      },
    });

  // 2. Watch both fields
  const fromYear = watch("fromYear");
  const toYear = watch("toYear");

  // 3. Auto-adjust To Year if From Year is selected after it
  useEffect(() => {
    if (fromYear && toYear) {
      const from = dayjs(fromYear);
      const to = dayjs(toYear);
      if (from.isValid() && to.isValid() && from.isAfter(to, "year")) {
        setValue("toYear", fromYear);
      }
    }
  }, [fromYear, toYear, setValue]);
  const showLoader = isFetching || isLoading;

  /* ----------------------- Build Recharts data ----------------- */

  const chartData = useMemo(() => {
    if (!data.length) return [];

    // All months sorted
    const months = _.uniq(
      data
        .map((x) => x.yearMonthName)
        .sort((a, b) => dayjs(a).unix() - dayjs(b).unix()),
    );

    const memberTypes = _.uniq(data.map((x) => x.memberTypeCode));

    return months.map((month) => {
      const row: any = { name: month };

      memberTypes.forEach((type) => {
        const record = data.find(
          (x) => x.yearMonthName === month && x.memberTypeCode === type,
        );
        row[type] = record?.totalIssue ?? 0;
      });

      return row;
    });
  }, [data]);

  /* ----------------------- Submit handler ---------------------- */

  const onSubmit = handleSubmit((formData) => {
    setFilterModel({
      fromYear: dayjs(formData.fromYear).format("YYYY"),
      toYear: dayjs(formData.toYear).format("YYYY"),
      actionType: formData.actionType,
    });
  });

  /* ----------------------- UI labels --------------------------- */

  const actionTitleMap: Record<number, string> = {
    1: "Issued Items per Month",
    2: "Returned Items per Month",
    3: "Renewed Items per Month",
  };

  /* ----------------------- Colors ------------------------------ */

  const RECHARTS_COLORS = [
    "#8884d8", // purple-blue
    "#82ca9d", // green
    "#ffc658", // yellow
    "#ff7f50", // coral
    "#8dd1e1", // light blue
    "#a4de6c", // light green
    "#d0ed57", // lime
    "#83a6ed", // blue
    "#8e4585", // violet
    "#ffbb28", // orange-yellow
    "#ff8042", // orange
    "#00C49F", // teal
    "#0088FE", // bright blue
    "#FFBB28", // gold
    "#FF8042", // deep orange
    "#AF19FF", // strong purple
    "#4DB6AC", // teal soft
    "#42A5F5", // blue soft
    "#AB47BC", // purple soft
    "#26A69A", // teal dark
    "#7E57C2", // indigo
    "#EC407A", // pink
    "#66BB6A", // green
    "#FFA726", // orange
    "#29B6F6", // sky blue
  ];

  const memberTypes = useMemo(
    () => _.uniq(data.map((x) => x.memberTypeCode)),
    [data],
  );

  /* ----------------------- Render ------------------------------ */

  return (
    <MainCard sx={{ mt: 1 }}>
      {/* Header */}
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={8}>
          <Typography variant="h3" display="flex" alignItems="center">
            <Icon sx={{ mr: 1 }}>
              <BarChartOutlined />
            </Icon>
            {actionTitleMap[filterModel.actionType ?? 1]}
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Stack direction="row" alignItems="center">
            <InputLabel
              sx={{
                fontWeight: "bold",
                whiteSpace: "nowrap",
                minWidth: 100,
              }}
            >
              Search Type:
            </InputLabel>

            <SelectField
              control={control}
              name="actionType"
              fullWidth
              disableClearable
              disabled={showLoader}
              options={[
                { id: 1, name: "Issued Items" },
                { id: 2, name: "Returned Items" },
                { id: 3, name: "Renewed Items" },
              ]}
            />
          </Stack>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Filters */}
      <Box component="form" onSubmit={onSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <DatePickerElement
              control={control}
              name="fromYear"
              label="From Year *"
              views={["year"]}
              format="YYYY"
              disableFuture
              minDate={dayjs("2000-01-01")}
              maxDate={
                toYear && dayjs(toYear).isValid() ? dayjs(toYear) : dayjs()
              }
              disabled={showLoader}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid item xs={4}>
            <DatePickerElement
              control={control}
              name="toYear"
              label="To Year *"
              views={["year"]}
              format="YYYY"
              disableFuture
              minDate={
                fromYear && dayjs(fromYear).isValid()
                  ? dayjs(fromYear)
                  : dayjs("2000-01-01")
              }
              disabled={showLoader}
              sx={{ width: "100%" }}
            />
          </Grid>

          <Grid item xs={4} display="flex" alignItems="center">
            <Button type="submit" variant="contained" disabled={showLoader}>
              {showLoader ? "Loading..." : "Show"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Chart */}
      <Box sx={{ height: 500 }}>
        {showLoader ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              {/* <XAxis dataKey="name" /> */}

              <XAxis
                dataKey="name"
                interval={0}
                angle={-30}
                textAnchor="end"
                height={70}
              />
              <YAxis />
              <Tooltip />
              <Legend />

              {memberTypes.map((type, index) => (
                <Bar
                  key={type}
                  dataKey={type}
                  stackId="a"
                  fill={RECHARTS_COLORS[index % RECHARTS_COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </MainCard>
  );
}
